import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { createPath } from "@odata/BindingContext";
import {
    IPrCSSZSubmissionLongtermCaregivingEntity,
    PrCareReceiverEntity,
    PrCSSZSubmissionCaregivingDetailEntity,
    PrCSSZSubmissionCaregivingEntity,
    PrCSSZSubmissionLongtermCaregivingDetailEntity,
    PrCSSZSubmissionLongtermCaregivingEntity,
    PrCSSZSubmissionPaymentBasisCaregivingEntity,
    PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import {
    handleIdentificationTypeBeforeSave,
    initIdentificationTypeLocalPath
} from "@pages/payroll/employee/Employee.utils";
import {
    addNewCaregivingIntervalLineItem,
    addWorkedInDaysIntervalLineItem,
    cleanUpCaregivingIntervalsBeforeSave,
    clearEntityAccordingToActionType,
    handleCaregivingActionsChange,
    handleIsCaringPersonallyChange,
    initReturnToWorkLocalContext,
    initWorkedInDays,
    PrCSSZSubmissionEntity,
    WorkedInDaysLocalPath
} from "@pages/payroll/submission/submissionTypes/PrCSSZSubmission.utils";
import PrCSSZSubmissionNEMPRIFormView from "@pages/payroll/submission/submissionTypes/PrCSSZSubmissionNEMPRIFormView";

import { withPermissionContext } from "../../../../contexts/permissionContext/withPermissionContext";

class PrCSSZSubmissionLongTermCaregivingFormView extends PrCSSZSubmissionNEMPRIFormView<IPrCSSZSubmissionLongtermCaregivingEntity> {
    typeCode = PrSubmissionTypeCode.EmployersNotificationOfAnEmployeesBenefitApplication;
    subTypeCode = PrSubmissionSubtypeCode.NempriLongTermCaregiving;

    typeSpecificOnAfterLoad(isNew: boolean): Promise<void> {
        const { storage } = this.props;

        // init local contexts from data, todo: only when isStartType?
        initIdentificationTypeLocalPath(storage,
            storage.data.bindingContext
                .navigate(PrCSSZSubmissionLongtermCaregivingEntity.Detail)
                .navigate(PrCSSZSubmissionLongtermCaregivingDetailEntity.CareReceiver),
            PrCareReceiverEntity.IdentificationNumber
        );

        initReturnToWorkLocalContext(storage);
        initWorkedInDays(storage);
        if (!storage.data.entity.Detail?.CaregivingIntervals?.length) {
            addNewCaregivingIntervalLineItem(storage, storage.data.bindingContext.navigate(createPath(PrCSSZSubmissionEntity.Detail, PrCSSZSubmissionCaregivingDetailEntity.CaregivingIntervals)));
        }

        return super.typeSpecificOnAfterLoad(isNew);
    }

    toggleLineItemVisibility(value: boolean, bc: BindingContext, collectionName: PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity) {
        const { storage } = this.props;
        const collectionBc = bc.getParent().navigate(collectionName);
        if (value) {
            // create first empty line item
            addWorkedInDaysIntervalLineItem(storage, collectionBc);
        } else {
            // remove all line items
            storage.setValue(collectionBc, []);
        }
        storage.refreshGroup(bc);
    }

    handleChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();

        handleCaregivingActionsChange(e, storage);
        handleIsCaringPersonallyChange(e, storage);

        if (path === PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.HasPlannedShifts) {
            this.toggleLineItemVisibility(!!e.value, e.bindingContext, PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkScheduleDays);
        }

        if (path === PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.HasWorkLeave) {
            this.toggleLineItemVisibility(!!e.value, e.bindingContext, PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkLeaveDays);
        }

        if (path === WorkedInDaysLocalPath) {
            // e.bindingContext is localContext with different path - create a correct one
            const bc = storage.data.bindingContext
                .navigate(PrCSSZSubmissionCaregivingEntity.Detail)
                .navigate(PrCSSZSubmissionCaregivingDetailEntity.PaymentBasis)
                .navigate(PrCSSZSubmissionPaymentBasisCaregivingEntity.WorkedInDays);
            this.toggleLineItemVisibility(!!e.value, bc, PrCSSZSubmissionPaymentBasisLongtermCaregivingEntity.WorkedInDays);
        }

        if (path === PrCSSZSubmissionCaregivingDetailEntity.IsStart && e.value === true) {
            this.initDecisivePeriods(true);
        }

        super.handleChange(e);
        // keep after storage -> handleChange to have updated data
    }


    onBeforeSave(data?: IPrCSSZSubmissionLongtermCaregivingEntity): IPrCSSZSubmissionLongtermCaregivingEntity {
        data = super.onBeforeSave(data);
        const { storage } = this.props;

        const careReceiverBc = storage.data.bindingContext
            .navigate(PrCSSZSubmissionLongtermCaregivingEntity.Detail)
            .navigate(PrCSSZSubmissionLongtermCaregivingDetailEntity.CareReceiver);
        handleIdentificationTypeBeforeSave(data, storage.data.bindingContext, careReceiverBc, {
            identificationPropName: PrCareReceiverEntity.IdentificationNumber
        });

        data = cleanUpCaregivingIntervalsBeforeSave(data, storage);
        data = clearEntityAccordingToActionType(data, storage);

        return data;
    }
}

export default withPermissionContext(PrCSSZSubmissionLongTermCaregivingFormView);